<template>
  <v-card outlined class="rounded-xl overflow-hidden">
    <v-card-title class="pa-5 pa-sm-6 align-start">
      <v-avatar color="primary" size="42" class="mr-4">
        <v-icon dark>mdi-account-group</v-icon>
      </v-avatar>
      <div>
        <div class="headline">Organization users</div>
        <div class="body-2 text--secondary mt-1">
          Manage who can access this organization. Removing access does not delete
          a person's account or their access to other organizations.
        </div>
      </div>
    </v-card-title>

    <v-divider />

    <v-card-text class="pa-4 pa-sm-6">
      <v-alert v-if="error" type="error" text dense class="mb-4">
        <div class="d-flex align-center">
          <span>{{ error }}</span>
          <v-spacer />
          <v-btn text small color="error" @click="getList">Retry</v-btn>
        </div>
      </v-alert>

      <div class="d-flex align-center flex-wrap mb-4">
        <v-text-field
          v-model.trim="search"
          label="Search users"
          prepend-inner-icon="mdi-magnify"
          clearable
          dense
          outlined
          hide-details
          class="user-list__search"
          aria-label="Search organization users"
        />
        <v-spacer />
        <div class="body-2 text--secondary mt-3 mt-sm-0">
          {{ count }} {{ count === 1 ? "user" : "users" }}
        </div>
      </div>

      <v-data-table
        :headers="headers"
        :items="users"
        :options.sync="pagination"
        :server-items-length="count"
        :loading="loading"
        :items-per-page="pagination.itemsPerPage"
        class="user-list__table"
        mobile-breakpoint="720"
      >
        <template v-slot:progress>
          <v-progress-linear indeterminate color="primary" />
        </template>

        <template v-slot:item.name="{ item }">
          <div class="d-flex align-center py-2">
            <v-avatar color="primary" size="36" class="mr-3">
              <span class="white--text text-uppercase body-2 font-weight-medium">
                {{ initials(item) }}
              </span>
            </v-avatar>
            <div>
              <div class="font-weight-medium">{{ displayName(item) }}</div>
              <div class="caption text--secondary hidden-sm-and-up">
                {{ item.email || "No email" }}
              </div>
            </div>
          </div>
        </template>

        <template v-slot:item.email="{ item }">
          <span>{{ item.email || "Not provided" }}</span>
        </template>

        <template v-slot:item.phoneNumber="{ item }">
          <span class="text--secondary">{{ item.phoneNumber || "Not provided" }}</span>
        </template>

        <template v-slot:item.access="{ item }">
          <v-chip v-if="isOwner(item)" color="primary" small outlined>
            <v-icon left small>mdi-shield-account</v-icon>
            Owner
          </v-chip>
          <v-chip v-else small outlined>
            <v-icon left small>mdi-account-check</v-icon>
            Member
          </v-chip>
        </template>

        <template v-slot:item.action="{ item }">
          <v-menu v-if="canEdit(item) || canRemove(item)" bottom left offset-y>
            <template v-slot:activator="{ on, attrs }">
              <v-btn icon v-bind="attrs" v-on="on" :aria-label="`Actions for ${displayName(item)}`">
                <v-icon>mdi-dots-vertical</v-icon>
              </v-btn>
            </template>
            <v-list dense>
              <v-list-item v-if="canEdit(item)" @click="editUser(item)">
                <v-list-item-icon><v-icon small>mdi-account-edit</v-icon></v-list-item-icon>
                <v-list-item-title>Edit profile</v-list-item-title>
              </v-list-item>
              <v-list-item v-if="canRemove(item)" @click="removeAccess(item)">
                <v-list-item-icon><v-icon small color="error">mdi-account-minus</v-icon></v-list-item-icon>
                <v-list-item-title class="error--text">Remove access</v-list-item-title>
              </v-list-item>
            </v-list>
          </v-menu>
          <v-tooltip v-else-if="isOwner(item)" left>
            <template v-slot:activator="{ on, attrs }">
              <v-icon small color="grey" v-bind="attrs" v-on="on">mdi-lock-outline</v-icon>
            </template>
            <span>The organization owner cannot be removed</span>
          </v-tooltip>
        </template>

        <template v-slot:no-data>
          <div class="py-10 text-center">
            <v-icon size="44" color="grey lighten-1">mdi-account-search-outline</v-icon>
            <div class="subtitle-1 mt-3">
              {{ search ? "No users match your search" : "No organization users found" }}
            </div>
            <div class="body-2 text--secondary mt-1">
              {{ search ? "Try a different name or email address." : "Invite a user below to grant access." }}
            </div>
          </div>
        </template>
      </v-data-table>
    </v-card-text>
  </v-card>
</template>

<script>
import { mapGetters } from "vuex";
import userService from "../service";

export default {
  name: "user-list",
  props: ["hideCreate"],
  data() {
    return {
      headers: [
        { text: "User", value: "name", sortable: false },
        { text: this.$t("labels.emailAddress"), value: "email", sortable: false },
        { text: this.$t("labels.phoneNumber"), value: "phoneNumber", sortable: false },
        { text: "Access", value: "access", sortable: false },
        { text: "", value: "action", sortable: false, align: "end", width: 56 },
      ],
      search: "",
      users: [],
      count: 0,
      loading: false,
      error: "",
      searchTimer: null,
      pagination: { page: 1, itemsPerPage: 10, sortBy: [], sortDesc: [] },
    };
  },
  computed: {
    ...mapGetters("global", ["currentUser"]),
  },
  created() { this.getList(); },
  watch: {
    search() {
      clearTimeout(this.searchTimer);
      this.searchTimer = setTimeout(() => {
        this.pagination.page = 1;
        this.getList();
      }, 300);
    },
    pagination: {
      handler() { this.getList(); },
      deep: true,
    },
  },
  beforeDestroy() { clearTimeout(this.searchTimer); },
  methods: {
    async getList() {
      this.loading = true;
      this.error = "";
      const { page, itemsPerPage, sortBy, sortDesc } = this.pagination;
      try {
        const response = await userService.getAll({
          limit: itemsPerPage,
          offset: itemsPerPage * (page - 1),
          search: this.search || undefined,
          orderBy: [sortBy[0] || "firstName", Boolean(sortDesc[0])],
        });
        this.users = response.data || [];
        this.count = response.count || 0;
        this.$emit("access-management", response.canManageAccess === true);
      } catch (error) {
        this.$emit("access-management", false);
        this.error = this.errorMessage(error, "Unable to load organization users.");
      } finally {
        this.loading = false;
      }
    },
    displayName(user) {
      return [user.firstName, user.lastName].filter(Boolean).join(" ") || user.email || "Unnamed user";
    },
    initials(user) {
      const value = [user.firstName, user.lastName].filter(Boolean).map(part => part[0]).join("");
      return value.slice(0, 2) || (user.email || "U")[0];
    },
    isOwner(user) {
      return user.isOwner === true || user.owner === true || user.membershipRole === "owner" || user.organizationRole === "owner";
    },
    canEdit(user) {
      return Number(this.currentUser && this.currentUser.id) === Number(user.id);
    },
    canRemove(user) {
      if (this.isOwner(user) || this.canEdit(user)) return false;
      return user.canRemoveAccess === true;
    },
    editUser(user) {
      this.$router.push({ name: "user-edit", params: { id: user.id } });
    },
    async removeAccess(user) {
      const result = await this.$swal.fire({
        title: "Remove organization access?",
        text: `${this.displayName(user)} will no longer be able to open this organization. Their account and access to other organizations will not be deleted.`,
        type: "warning",
        showCancelButton: true,
        confirmButtonText: "Remove access",
        cancelButtonText: "Cancel",
        confirmButtonColor: this.$vuetify.theme.currentTheme.error,
      });
      if (!result.value) return;
      try {
        await userService.removeAccess(user.id);
        this.$toasted.success("Organization access removed.", { position: "top-right", duration: 3500 });
        await this.getList();
      } catch (error) {
        this.$toasted.error(this.errorMessage(error, "Unable to remove access."), { position: "top-right", duration: 3500 });
      }
    },
    errorMessage(error, fallback) {
      return (error && error.data && (error.data.message || error.data)) ||
        (error && error.response && error.response.data && (error.response.data.message || error.response.data)) || fallback;
    },
  },
};
</script>

<style scoped>
.user-list__search { max-width: 360px; }
.user-list__table { border: 1px solid rgba(0, 0, 0, 0.12); border-radius: 12px; overflow: hidden; }
.theme--dark .user-list__table { border-color: rgba(255, 255, 255, 0.12); }
@media (max-width: 599px) { .user-list__search { max-width: none; flex-basis: 100%; } }
</style>
